import { useEffect, useRef, useState, useCallback } from 'react';

// TREATX-L1F-DNS-ACTIVATION (production correction) — same-origin SSE
// transport. Empty/undefined base yields a relative URL so the
// EventSource targets the page's own hostname (preserves the trusted
// X-Forwarded-Host used by the L1-F surface resolver).
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const RECONNECT_BASE_DELAY = 2000;
const RECONNECT_MAX_DELAY = 30000;

/**
 * SSE hook for real-time messaging events.
 * Connects to /api/v2/messaging/stream and handles:
 * - message_created → refetch thread messages
 * - message_read → update read status
 * - thread_escalated → badge update
 * - guard_flagged → show risk indicator
 *
 * Auto-reconnects with exponential backoff.
 * Uses Last-Event-ID for cursor-based replay.
 */
export function useMessagingSSE(userId, handlers = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const esRef = useRef(null);
  const lastEventIdRef = useRef(null);
  const attemptRef = useRef(0);
  const timerRef = useRef(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const disconnect = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const connect = useCallback(() => {
    if (!userId) return;
    disconnect();

    const params = new URLSearchParams({ user_id: userId });
    if (lastEventIdRef.current) {
      params.append('last_event_id', lastEventIdRef.current);
    }

    // FIND-H-08 · SSE cookie-only transport. `withCredentials: true`
    // makes the browser attach the HttpOnly `treatx_auth` cookie on the
    // long-lived stream connection. No JWT is passed via query string.
    // L1F-DNS-ACTIVATION: relative URL when no explicit base is set.
    const streamUrl = API_BASE_URL
      ? `${API_BASE_URL}/api/v2/messaging/stream?${params}`
      : `/api/v2/messaging/stream?${params}`;
    const es = new EventSource(streamUrl, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
      attemptRef.current = 0;
    };

    es.onerror = () => {
      es.close();
      esRef.current = null;
      setIsConnected(false);
      const delay = Math.min(
        RECONNECT_BASE_DELAY * Math.pow(2, attemptRef.current),
        RECONNECT_MAX_DELAY
      );
      attemptRef.current += 1;
      timerRef.current = setTimeout(connect, delay);
    };

    const listen = (eventType, fn) => {
      es.addEventListener(eventType, (e) => {
        try {
          const data = JSON.parse(e.data);
          lastEventIdRef.current = e.lastEventId || data.event_id;
          fn(data);
        } catch { /* ignore parse errors */ }
      });
    };

    listen('message_created', (d) => handlersRef.current.onMessageCreated?.(d));
    listen('message_read', (d) => handlersRef.current.onMessageRead?.(d));
    listen('thread_escalated', (d) => handlersRef.current.onThreadEscalated?.(d));
    listen('guard_flagged', (d) => handlersRef.current.onGuardFlagged?.(d));
    es.addEventListener('heartbeat', () => {});
    // MTMR-P0-CLOSURE — the server closes a stream whose active context no
    // longer resolves (workspace switched elsewhere / membership revoked).
    // Do NOT auto-reconnect into a stale Provider: tear down and let the
    // central context handler re-resolve the canonical workspace.
    es.addEventListener('context_invalid', () => {
      try { es.close(); } catch { /* noop */ }
      esRef.current = null;
      setIsConnected(false);
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      try {
        window.dispatchEvent(new CustomEvent('treatx:context-invalid', {
          detail: { code: 'CONTEXT_REVOKED', url: 'sse:/v2/messaging/stream' },
        }));
      } catch { /* noop */ }
    });
  }, [userId, disconnect]);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  // MTMR-P0-CLOSURE — central teardown signal (workspace switch, context
  // revoked, cross-tab context replacement). Closes the stream WITHOUT
  // scheduling a reconnect under the old context.
  useEffect(() => {
    const onClose = () => disconnect();
    window.addEventListener('treatx:realtime-close', onClose);
    return () => window.removeEventListener('treatx:realtime-close', onClose);
  }, [disconnect]);

  return { isConnected, reconnect: connect };
}
